import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import useDocumentTitle from '../lib/useDocumentTitle';
import RCLogo from '../components/Logo';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Loader2, KeyRound, CheckCircle2, AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

/**
 * `/reset-password?token=<raw-token>`. Landing page for the click in
 * the password-reset email. The token is the auth (no JWT header
 * needed); the backend validates it against the SHA-256 hash stored
 * server-side, then either accepts the new password or returns 400
 * with a generic "link ungültig oder abgelaufen" message.
 *
 * Three operating states:
 *   - `form`   . Token present, show the new-password fields
 *   - `success`. Password updated, show CTA back to /auth so the
 *                 user can log in with the new password
 *   - `error`  . Token missing OR backend rejected the submission
 *                 (expired / consumed / unknown). Shows a "request a
 *                 new link" path back to /auth?mode=forgot.
 *
 * We deliberately do NOT auto-log-in after a successful reset. A reset
 * flow that auto-issues a session has a slightly larger blast radius
 * if the email is intercepted; the small UX cost of manual login is
 * worth the smaller attack surface.
 */
export default function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  useDocumentTitle('Passwort zurücksetzen · Real Content');

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Separate toggles for each field. Lets the user verify the "new"
  // typing without revealing the "confirm" (the more common pattern
  // because confirm is meant to catch typos in NEW, not the reverse).
  // Defaults to hidden; opt-in per visit.
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  // Three top-level states drive the render. Keeping it as one string
  // (vs three booleans) prevents the "loading AND success" race that
  // would otherwise be possible if we forgot to flip a flag.
  const [state, setState] = useState(token ? 'form' : 'error');
  const [errorMessage, setErrorMessage] = useState(
    token ? '' : (t('resetPassword.missingToken') || 'Kein Token in der URL gefunden.')
  );

  // Live password policy. Same rules as the signup form (min 10
  // chars, ≥1 letter, ≥1 digit). The backend enforces the SAME
  // policy via the ResetPasswordRequest validator, so the inline
  // feedback exists only to save users a 422 round-trip.
  const pwLenOk = newPassword.length >= 10;
  const pwLetterOk = /[A-Za-z]/.test(newPassword);
  const pwDigitOk = /\d/.test(newPassword);
  const pwMatch = confirm.length > 0 && newPassword === confirm;
  const canSubmit =
    !submitting && token && pwLenOk && pwLetterOk && pwDigitOk && pwMatch;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setState('success');
      // Toast as well. Some users will skim past the in-card
      // confirmation and land on /auth wondering whether it worked.
      toast.success(
        t('resetPassword.successToast') || 'Passwort geändert.'
      );
    } catch (err) {
      // Backend returns generic "Link ungültig oder abgelaufen" for
      // any token-related failure. We surface that verbatim so the
      // user knows to request a new link. For complexity violations
      // (422 from Pydantic) surface the detail too.
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : (t('resetPassword.failed') || 'Etwas ist schiefgelaufen. Versuche es erneut.');
      // If the backend told us the token is bad, switch to the error
      // state (no point re-submitting the same token). For password-
      // policy violations stay in the form so the user can fix it.
      const looksLikeTokenError = /ung[üu]ltig|abgelaufen|invalid|expired/i.test(msg);
      if (looksLikeTokenError) {
        setState('error');
        setErrorMessage(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f7f3ea] px-4 relative overflow-hidden">
      {/* Same animated background as AuthPage so the visual continuity
          from the email click is obvious. */}
      <div className="absolute inset-0 bg-gradient-to-tr from-[#1f4d3a]/10 via-transparent to-transparent pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#dfe8db] rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#f0e6cf] rounded-full blur-3xl pointer-events-none" />

      <div className="w-full max-w-md mx-4 z-10">
        <div className="text-center mb-8">
          <div
            className="flex items-center justify-center mb-3 cursor-pointer"
            onClick={() => navigate('/')}
            role="button"
            aria-label="Real Content"
          >
            <RCLogo size="lg" />
          </div>
        </div>

        <Card className="glass border-[#e3dccb] shadow-2xl">
          {state === 'form' && (
            <>
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-center inline-flex items-center justify-center gap-2">
                  <KeyRound className="w-6 h-6 text-[#1f4d3a]" />
                  {t('resetPassword.title') || 'Neues Passwort wählen'}
                </CardTitle>
                <CardDescription className="text-center">
                  {t('resetPassword.subtitle')
                    || 'Wähle ein neues Passwort für dein Konto. Du wirst danach automatisch zur Anmeldung weitergeleitet.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                      {t('resetPassword.newPasswordLabel') || 'Neues Passwort'}
                    </label>
                    <div className="relative">
                      <Input
                        data-testid="reset-pw-new"
                        type={showNew ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={10}
                        autoComplete="new-password"
                        className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] focus:ring-[#1f4d3a]/20 pr-10"
                      />
                      <button
                        type="button"
                        data-testid="reset-pw-new-toggle"
                        onClick={() => setShowNew((v) => !v)}
                        aria-label={
                          showNew
                            ? (t('auth.hidePassword') || 'Passwort verbergen')
                            : (t('auth.showPassword') || 'Passwort anzeigen')
                        }
                        aria-pressed={showNew}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#8a8474] hover:text-[#4a463b] transition-colors rounded focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
                      >
                        {showNew ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {/* Live policy rules. Mirror AuthPage signup variant
                        so the user sees the same checklist either way.
                        Each rule flips slate→emerald as it's satisfied. */}
                    {newPassword.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
                        <span className={pwLenOk ? 'text-[#1e7a4f]' : 'text-[#8a8474]'}>
                          {pwLenOk ? '✓' : '○'} {t('auth.passwordRuleLength') || 'Mind. 10 Zeichen'}
                        </span>
                        <span className={pwLetterOk ? 'text-[#1e7a4f]' : 'text-[#8a8474]'}>
                          {pwLetterOk ? '✓' : '○'} {t('auth.passwordRuleLetter') || 'Ein Buchstabe'}
                        </span>
                        <span className={pwDigitOk ? 'text-[#1e7a4f]' : 'text-[#8a8474]'}>
                          {pwDigitOk ? '✓' : '○'} {t('auth.passwordRuleDigit') || 'Eine Ziffer'}
                        </span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-sm font-medium text-[#23211a] mb-1.5 block">
                      {t('resetPassword.confirmLabel') || 'Passwort bestätigen'}
                    </label>
                    <div className="relative">
                      <Input
                        data-testid="reset-pw-confirm"
                        type={showConfirm ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={confirm}
                        onChange={(e) => setConfirm(e.target.value)}
                        required
                        autoComplete="new-password"
                        className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] focus:ring-[#1f4d3a]/20 pr-10"
                      />
                      <button
                        type="button"
                        data-testid="reset-pw-confirm-toggle"
                        onClick={() => setShowConfirm((v) => !v)}
                        aria-label={
                          showConfirm
                            ? (t('auth.hidePassword') || 'Passwort verbergen')
                            : (t('auth.showPassword') || 'Passwort anzeigen')
                        }
                        aria-pressed={showConfirm}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#8a8474] hover:text-[#4a463b] transition-colors rounded focus:outline-none focus:ring-2 focus:ring-[#1f4d3a]/40"
                      >
                        {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {confirm.length > 0 && !pwMatch && (
                      <p className="text-[11px] text-[#b3543f] mt-1.5">
                        {t('resetPassword.mismatch') || 'Die Passwörter stimmen nicht überein.'}
                      </p>
                    )}
                  </div>

                  <Button
                    data-testid="reset-pw-submit"
                    type="submit"
                    disabled={!canSubmit}
                    className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        {t('resetPassword.submitting') || 'Wird gespeichert…'}
                      </>
                    ) : (
                      <>{t('resetPassword.submit') || 'Passwort speichern'}</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </>
          )}

          {state === 'success' && (
            <CardContent className="py-10 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#e4f0e8] mx-auto">
                <CheckCircle2 className="w-8 h-8 text-[#1e7a4f]" />
              </div>
              <h2 className="text-xl font-semibold text-[#23211a]">
                {t('resetPassword.successTitle') || 'Passwort geändert'}
              </h2>
              <p className="text-sm text-[#6f6a5c]">
                {t('resetPassword.successBody')
                  || 'Du kannst dich jetzt mit deinem neuen Passwort anmelden.'}
              </p>
              <Link to="/auth" className="block pt-2">
                <Button className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white">
                  {t('resetPassword.toLogin') || 'Zur Anmeldung'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          )}

          {state === 'error' && (
            <CardContent className="py-10 text-center space-y-4">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#f7e8e3] mx-auto">
                <AlertCircle className="w-8 h-8 text-[#b3543f]" />
              </div>
              <h2 className="text-xl font-semibold text-[#23211a]">
                {t('resetPassword.errorTitle') || 'Link ungültig oder abgelaufen'}
              </h2>
              <p className="text-sm text-[#6f6a5c]">
                {errorMessage
                  || t('resetPassword.errorBody')
                  || 'Bitte fordere einen neuen Link an.'}
              </p>
              {/* Take them straight to the forgot-password mode of /auth
                  via the `mode` query param. AuthPage reads it on mount. */}
              <Link to="/auth?mode=forgot" className="block pt-2">
                <Button className="w-full bg-[#1f4d3a] hover:bg-[#163a2c] text-white">
                  {t('resetPassword.requestNew') || 'Neuen Link anfordern'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
}
